/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import CardLayout from "../Cards/CardLayout";
import { useFetchData } from "../../customHooks/useGetDashboardData";
import Loader from "../feedBacks/loader";
import CardError from "../feedBacks/CardError";
import NoContent from "../feedBacks/NoContent";
import { EChartsOption } from "echarts";
import { REFETCH_CONFIGURATION } from "../../utils/constants";
import { createToolboxConfig } from "../../utils/utils";
import NoDeviceCardError from "../feedBacks/NoDeviceCardError";
import { useUtils } from "../../customHooks/useUtils";

interface IPowerConsumptionData {
  dataset: [string, number][];
  total_consumption: number;
}

const PowerConsumptionBySite = ({ company_id, startDate, endDate }: { company_id: string; startDate: string; endDate: string; }) => {
  const { FEEDBACK_MESSAGES, t } = useUtils();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
 
  const {
    data: powerConsumptionData,
    isLoading: isDataLoading,
    error,
    refetch,
  } = useFetchData(
    ["/operations", "fetchPowerConsumption", company_id],
    "/operations/power-consumption-chart",
    {
      company_id,
      start_date: startDate,
      end_date: endDate,
    },
    // @ts-ignore
    {
      ...REFETCH_CONFIGURATION,
    }
  );

  const transformedData = powerConsumptionData
    ? (powerConsumptionData as IPowerConsumptionData).dataset
        .slice(1)
        .map(([title, value]) => ({
          title,
          consumption: value,
        }))
    : [];

  const isLoading =
    // Show loading for initial page load
    isDataLoading ||
    // Show loading when filter is applied and when actually refetching
    isLocalLoading;

  useEffect(() => {
    if (startDate && endDate) {
      setIsLocalLoading(true);

      refetch().finally(() => {
        setIsLocalLoading(false);
      });
    }
  }, [startDate, endDate, refetch]);
  // Create properly formatted data for the chart
  const chartData = transformedData.map((item) => item.consumption);
  const chartLabels = transformedData.map((item) => item.title);

  const options: EChartsOption = {
    ...createToolboxConfig({
      chartName: t("chart.power_consumption_by_site"),
      top: "50px",
      right: "2px",
    }),
    grid: {
      left: "5%",
      right: "15%",
      top: "3%",
      bottom: "3%",
      containLabel: true,
    },
    xAxis: {
      max: "dataMax",
    },
    yAxis: {
      type: "category",
      data: chartLabels.length > 0 ? chartLabels : [t("feedback_msg.no_data")],
      inverse: true,
      animationDuration: 300,
      animationDurationUpdate: 300,
      axisLabel: {
        fontWeight: "bold",
        fontSize: 11,
        interval: 0, // Force show all labels
        width: 120,
        overflow: "truncate",
        align: "right",
      },
    },
    series: [
      {
        realtimeSort: true,
        name: t("chart.power_consumption_by_site"),
        type: "bar",
        color: "#9fdef1",
        data: chartData,
        label: {
          show: true,
          position: "right",
          valueAnimation: true,
          formatter: "{c}kWh",
        },
      },
    ],
    style: {
      overflow: "visible",
    }, // legend: {
    //   show: true,
    // },
    animationDuration: 0,
    animationDurationUpdate: 3000,
    animationEasing: "linear",
    animationEasingUpdate: "linear",
  };

  return (
    <CardLayout title={t("chart.power_consumption_by_site")} className="flex-1">
      <div className="flex flex-col h-full w-full">
        {isLoading ? (
          <div className="flex justify-center items-center h-full w-full">
            <Loader />
          </div>
        ) : error ? (
          (error as any).response.data.detail ? (
            <NoDeviceCardError
              message={FEEDBACK_MESSAGES.NO_DEVICE}
              className="!max-h-[150px]"
            />
          ) : (
            <CardError
              message={FEEDBACK_MESSAGES.ERROR}
              className="!h-[100px]"
            />
          )
        ) : transformedData.length === 0 ? (
          <NoContent message={FEEDBACK_MESSAGES.NO_DATA} />
        ) : (
          <div className="h-full w-full relative">
            <ReactECharts option={options} />
          </div>
        )}
      </div>
    </CardLayout>
  );
};

export default PowerConsumptionBySite;
